import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Container } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateUserConfig } from '../../redux/configuration/action';
import { publishableKeySelector, userConfigurationSelector, userPaymentMethodsSelector } from '../EsSettings/selector';
import { AccountBilling as AccountBillingComponent } from '../../components/AccountSettings';
import { IPaymentCard } from '../../types/global/generic.types';
import {
    deletePaymentMethod,
    getPaymentMethods,
    getPaymentRail,
    getPublishableKey,
    verifyFlutterwaveBillingCard,
} from '../../redux/payment/action';
import { isExpired } from '../../components/AccountSettings/AccountBilling';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { defaultCheckoutProvider, resolvePaymentRail } from '../../utils/paymentRail';
import type { BillingPaymentProvider } from '../../utils/billingPaymentRailCopy';
import {
    paymentMethodMatchesBillingRail,
    readFlutterwaveBillingReturnParams,
    shouldVerifyFlutterwaveBillingReturn,
} from '../../utils/billingFlutterwaveReturn';
import { ROUTES } from '../../types/global/routes.types';

const styles = StyleSheet.create({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100%',
    },
});

const AccountBilling = () => {
    const configuration = useSelector(userConfigurationSelector);
    const paymentMethods = useSelector(userPaymentMethodsSelector);
    const { publishableKey } = useSelector(publishableKeySelector);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const location = useLocation();
    const navigate = useNavigate();
    const [provider, setProvider] = useState<BillingPaymentProvider>('stripe');
    const verifyingFlutterwaveRef = useRef(false);

    const handleUpdatePreference = (payload: any) => {
        dispatch(
            updateUserConfig({
                payload,
            }),
        );
    };

    const loadBillingRail = useCallback(() => {
        dispatchPromise(getPaymentRail({ category: 'subscriptions' }))
            .then((railData: unknown) => {
                const rail = resolvePaymentRail(railData as any);
                const next = defaultCheckoutProvider(rail);
                setProvider(next);
                const keySource = next === 'paystack' ? 'paystack' : next === 'flutterwave' ? 'flutterwave' : 'stripe';
                dispatch(getPublishableKey({ source: keySource }));
            })
            .catch(() => {
                // Keep Stripe as last-resort only when rail is unavailable and country is unknown.
                setProvider('stripe');
                dispatch(getPublishableKey({ source: 'stripe' }));
            });
        dispatch(getPaymentMethods());
    }, [dispatch, dispatchPromise]);

    useEffect(() => {
        loadBillingRail();
    }, [loadBillingRail]);

    useEffect(() => {
        if (!shouldVerifyFlutterwaveBillingReturn(location.search)) {
            const { transactionId, txRef, status } = readFlutterwaveBillingReturnParams(location.search);
            const looksLikeReturn = Boolean(transactionId || txRef || status);
            if (looksLikeReturn) {
                navigate(ROUTES.ACCOUNT_BILLING, { replace: true });
                if (status && status !== 'successful' && status !== 'success' && status !== 'completed') {
                    showToastMessage('Card check was not completed.', 'info');
                }
            }
            return;
        }
        if (verifyingFlutterwaveRef.current) return;

        const { transactionId, txRef } = readFlutterwaveBillingReturnParams(location.search);
        verifyingFlutterwaveRef.current = true;
        void (async () => {
            try {
                await dispatchPromise(
                    verifyFlutterwaveBillingCard({
                        ...(transactionId ? { transaction_id: transactionId } : {}),
                        ...(txRef ? { tx_ref: txRef } : {}),
                    }),
                );
                dispatch(getPaymentMethods());
            } catch {
                showToastMessage('Could not verify Flutterwave card. Try again.', 'error');
            } finally {
                navigate(ROUTES.ACCOUNT_BILLING, { replace: true });
                verifyingFlutterwaveRef.current = false;
            }
        })();
    }, [dispatch, dispatchPromise, location.search, navigate]);

    const handleDeleteCard = (card: IPaymentCard, isDefaultCard?: boolean) => {
        const railCards = (paymentMethods ?? []).filter(c => paymentMethodMatchesBillingRail(c, provider));
        if (isDefaultCard && railCards.length > 1) {
            const nextAvailableCard = railCards.find(
                c =>
                    c._id !== card._id && !isExpired(c.authorization?.exp_month ?? '', c.authorization?.exp_year ?? ''),
            );
            if (nextAvailableCard) {
                handleUpdatePreference({
                    default_payment_method: nextAvailableCard._id,
                });
            }
            dispatch(
                deletePaymentMethod({
                    documentId: card._id,
                }),
            );
        } else {
            dispatch(
                deletePaymentMethod({
                    documentId: card._id,
                }),
            );
        }
    };

    const ready = Boolean(publishableKey) || provider === 'flutterwave';
    const railPaymentMethods = (paymentMethods ?? []).filter(card => paymentMethodMatchesBillingRail(card, provider));

    if (!ready) return null;

    return (
        <Container disableGutters style={styles.container} maxWidth={false}>
            <AccountBillingComponent
                onChange={payload => handleUpdatePreference(payload)}
                setting={configuration}
                publicKey={publishableKey || 'flutterwave'}
                provider={provider}
                isNonUs={provider === 'paystack'}
                paymentMethods={railPaymentMethods}
                onMakeDefault={card => {
                    handleUpdatePreference({
                        default_payment_method: card._id,
                    });
                }}
                onDeleteCard={handleDeleteCard}
            />
        </Container>
    );
};

export { AccountBilling };
